/**
 * Cervus Botica — cart drawer + cintillo + PDP commerce
 */
(function () {
  const moneyFormat = window.Shopify?.currency?.active
    ? `${window.Shopify.currency.active} {{amount}}`
    : '{{amount}}';

  function formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, moneyFormat);
    }
    return (cents / 100).toFixed(2);
  }

  function escapeHtml(value) {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;');
  }

  function getCartVariantLabel(variantTitle) {
    if (variantTitle == null) return '';

    const title = String(variantTitle).trim();
    if (!title) return '';

    const normalized = title.toLowerCase();
    if (normalized === 'null' || normalized === 'undefined' || title === 'Default Title') return '';

    return `<p class="cart-drawer__item-variant type-body-sm">${escapeHtml(title)}</p>`;
  }

  /* —— Cintillo —— */
  const cintillo = document.querySelector('[data-editorial-cintillo]');
  if (cintillo) {
    const slides = cintillo.querySelectorAll('[data-cintillo-slide]');
    const interval = Number(cintillo.dataset.cintilloInterval) || 7000;
    let index = 0;

    if (slides.length > 1 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      window.setInterval(() => {
        slides[index].classList.remove('is-active');
        index = (index + 1) % slides.length;
        slides[index].classList.add('is-active');
      }, interval);
    }
  }

  /* —— Cart drawer —— */
  const drawer = document.querySelector('[data-cart-drawer]');
  const labelDecrease = drawer?.dataset.labelDecrease || 'Decrease quantity';
  const labelIncrease = drawer?.dataset.labelIncrease || 'Increase quantity';
  const labelQuantity = drawer?.dataset.labelQuantity || 'Quantity';
  const labelRemove = drawer?.dataset.labelRemove || 'Remove';
  const openButtons = document.querySelectorAll('[data-cart-drawer-open]');
  const closeTargets = document.querySelectorAll('[data-cart-drawer-close]');
  const itemsRoot = drawer?.querySelector('[data-cart-items]');
  const emptyWrap = drawer?.querySelector('[data-cart-empty-wrap]');
  const footer = drawer?.querySelector('[data-cart-footer]');
  const subtotalEl = drawer?.querySelector('[data-cart-subtotal]');
  const subtotalRow = drawer?.querySelector('[data-cart-subtotal-row]');
  const cartCta = drawer?.querySelector('[data-cart-cta]');
  const accountUrl = drawer?.dataset.accountUrl || '/account';
  const statusEl = drawer?.querySelector('[data-cart-status]');
  const countEls = document.querySelectorAll('[data-cart-count]');
  let drawerOpen = false;
  let cartBusy = false;
  let cartScrollLockY = 0;
  let cartCloseTimer = null;
  let cartCloseTransitionHandler = null;
  let isClosingCart = false;
  let cartCloseInProgress = false;
  let isOpeningCart = false;
  let cartFetchPromise = null;
  let lastCartFingerprint = null;
  const mobileCartMq = window.matchMedia('(max-width: 47.99rem)');

  function isMobileCartViewport() {
    return mobileCartMq.matches;
  }

  function getMobileCartCloseDurationMs() {
    if (!drawer) return 368;

    const styles = getComputedStyle(drawer);
    const duration = Number.parseFloat(styles.getPropertyValue('--cart-mobile-drawer-transform-duration')) || 320;
    return duration + 48;
  }

  function getCartCloseDurationMs() {
    if (isMobileCartViewport()) return getMobileCartCloseDurationMs();

    if (!drawer) return 420;

    const styles = getComputedStyle(drawer);
    const drawerDuration = Number.parseFloat(styles.getPropertyValue('--cart-drawer-duration')) || 380;
    return drawerDuration + 48;
  }

  function cancelCartCloseAnimation() {
    if (cartCloseTimer) {
      window.clearTimeout(cartCloseTimer);
      cartCloseTimer = null;
    }

    const closeSurface = isMobileCartViewport()
      ? drawer
      : drawer?.querySelector('.cart-drawer__panel');
    if (closeSurface && cartCloseTransitionHandler) {
      closeSurface.removeEventListener('transitionend', cartCloseTransitionHandler);
      cartCloseTransitionHandler = null;
    }
  }

  function scheduleCartDrawerCloseFinish() {
    cancelCartCloseAnimation();

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finishCartDrawerClose();
      return;
    }

    const closeSurface = isMobileCartViewport() ? drawer : drawer?.querySelector('.cart-drawer__panel');
    let finished = false;

    const complete = () => {
      if (finished) return;
      finished = true;
      cancelCartCloseAnimation();
      finishCartDrawerClose();
    };

    if (closeSurface) {
      cartCloseTransitionHandler = (event) => {
        if (event.target !== closeSurface || event.propertyName !== 'transform') return;
        complete();
      };
      closeSurface.addEventListener('transitionend', cartCloseTransitionHandler);
    }

    cartCloseTimer = window.setTimeout(complete, getCartCloseDurationMs());
  }

  function disableMobileCartDrawerHitTargets() {
    if (!isMobileCartViewport() || !drawer) return;

    drawer.querySelector('.cart-drawer__overlay')?.style.setProperty('pointer-events', 'none');
    drawer.querySelector('.cart-drawer__panel')?.style.setProperty('pointer-events', 'none');
  }

  function restoreMobileCartDrawerHitTargets() {
    if (!isMobileCartViewport() || !drawer) return;

    drawer.querySelector('.cart-drawer__overlay')?.style.removeProperty('pointer-events');
    drawer.querySelector('.cart-drawer__panel')?.style.removeProperty('pointer-events');
  }

  function resetMobileCartPageInteraction() {
    if (!isMobileCartViewport()) return;

    disableMobileCartDrawerHitTargets();

    const html = document.documentElement;
    const main = document.getElementById('main-content');
    const extraLockClasses = [
      'overflow-hidden',
      'no-scroll',
      'is-locked',
      'body-locked',
      'cart-open',
      'cart-drawer-open',
      'drawer-open',
      'checkout-open',
      'mobile-cart-open',
      'is-closing',
    ];

    extraLockClasses.forEach((className) => {
      document.body.classList.remove(className);
      html.classList.remove(className);
      main?.classList.remove(className);
    });

    drawer?.classList.remove('is-open', 'is-closing');

    html.style.overflow = '';
    html.style.height = '';
    html.style.overscrollBehavior = '';
    html.style.pointerEvents = '';
    html.style.touchAction = '';

    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.height = '';
    document.body.style.touchAction = '';
    document.body.style.pointerEvents = '';
    document.body.style.overscrollBehavior = '';
  }

  function clearCartDrawerChrome() {
    window.cervusBodyLock?.set?.('cart', false);

    const html = document.documentElement;
    const main = document.getElementById('main-content');
    const lockClasses = [
      'cart-open',
      'cart-drawer-open',
      'drawer-open',
      'checkout-open',
      'mobile-cart-open',
      'is-cart-open',
      'is-active',
      'overlay-open',
      'header-drawer-open',
      'header-light',
      'overflow-hidden',
      'no-scroll',
      'is-locked',
      'body-locked',
      'is-closing',
    ];

    lockClasses.forEach((className) => {
      document.body.classList.remove(className);
      html.classList.remove(className);
      main?.classList.remove(className);
    });

    document.body.style.touchAction = '';
    document.body.style.overscrollBehavior = '';
    document.body.style.pointerEvents = '';
    html.style.overflow = '';
    html.style.height = '';
    html.style.overscrollBehavior = '';
    html.style.pointerEvents = '';
    html.style.touchAction = '';
    html.classList.remove(
      'cart-drawer-scroll-lock',
      'overflow-hidden',
      'no-scroll',
      'is-locked',
      'body-locked'
    );
    delete document.body.dataset.cartScrollLocked;
    delete document.body.dataset.scrollY;

    drawer?.classList.remove('is-open', 'is-closing');

    if (isMobileCartViewport()) {
      resetMobileCartPageInteraction();
    }
  }

  function releaseMobileCartScrollLock(savedY) {
    const html = document.documentElement;

    html.style.scrollBehavior = 'auto';

    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.height = '';
    document.body.style.overflow = '';
    document.body.style.touchAction = '';
    document.body.style.pointerEvents = '';
    document.body.style.overscrollBehavior = '';

    html.classList.remove('cart-drawer-scroll-lock');
    delete document.body.dataset.cartScrollLocked;
    delete document.body.dataset.scrollY;

    window.scrollTo(0, savedY);

    html.style.scrollBehavior = '';
  }

  function finishCartDrawerClose() {
    cancelCartCloseAnimation();

    const savedY = readLockedScrollY();
    const html = document.documentElement;

    if (isMobileCartViewport()) {
      disableMobileCartDrawerHitTargets();
      drawer?.classList.remove('is-open', 'is-closing');

      window.cervusHeaderScroll?.syncScrollY?.(savedY);
      releaseMobileCartScrollLock(savedY);
      window.cervusBodyLock?.set?.('cart', false);

      html.style.overflow = '';
      html.style.height = '';
      html.style.overscrollBehavior = '';
      html.style.pointerEvents = '';
      html.style.touchAction = '';
      html.classList.remove('overflow-hidden', 'no-scroll', 'is-locked', 'body-locked');

      document.body.classList.remove(
        'mobile-cart-open',
        'checkout-open',
        'drawer-open',
        'overflow-hidden',
        'no-scroll',
        'is-locked',
        'body-locked',
        'is-closing'
      );

      cartScrollLockY = savedY;
      isClosingCart = false;
      cartCloseInProgress = false;
      syncCartNavChrome(false);
      restoreMobileCartDrawerHitTargets();
      return;
    }

    html.style.scrollBehavior = 'auto';

    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.height = '';
    document.body.style.overflow = '';
    document.body.style.touchAction = '';
    document.body.style.pointerEvents = '';

    clearCartDrawerChrome();

    try {
      window.scrollTo({ top: savedY, left: 0, behavior: 'instant' });
    } catch {
      window.scrollTo(0, savedY);
    }

    html.style.scrollBehavior = '';

    cartScrollLockY = savedY;
    window.cervusHeaderScroll?.syncScrollY?.(savedY);
    syncCartNavChrome(false);
  }

  function openMobileCartDrawerVisual() {
    window.cervusMobileDrawer?.openVisual?.(drawer);
  }

  function portalCartDrawer() {
    if (!drawer) return;

    if (drawer.parentElement !== document.documentElement) {
      document.documentElement.appendChild(drawer);
    }
  }

  function lockCartScroll() {
    if (window.cervusNavChrome?.isMobileOverlayViewport?.()) {
      window.cervusNavChrome.syncMobileHeaderTotalHeight?.();
    }

    cartScrollLockY = window.scrollY;
    document.documentElement.classList.add('cart-drawer-scroll-lock');
    document.body.dataset.cartScrollLocked = 'true';
    document.body.dataset.scrollY = String(cartScrollLockY);
    document.body.style.position = 'fixed';
    document.body.style.top = `-${cartScrollLockY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.height = 'auto';
    document.body.style.overflow = 'hidden';
  }

  function readLockedScrollY() {
    const fromTop = Math.abs(Number.parseInt(document.body.style.top || '0', 10));
    if (fromTop > 0) return fromTop;
    return Number.parseInt(document.body.dataset.scrollY || String(cartScrollLockY), 10) || 0;
  }

  function cartImageUrl(url) {
    if (!url) return '';
    try {
      const parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('width', '240');
      return parsed.toString();
    } catch {
      return url;
    }
  }

  function isSearchOverlayOpen() {
    return document.querySelector('[data-search-overlay]')?.classList.contains('is-open') === true;
  }

  function announce(message) {
    if (statusEl) statusEl.textContent = message;
  }

  function syncCartNavChrome(isOpen) {
    if (!window.cervusNavChrome?.isMobileOverlayViewport?.()) return;

    window.cervusNavChrome.syncMobileHeaderTotalHeight?.();

    if (isOpen) {
      window.cervusNavChrome.syncSolid?.(true);
      return;
    }

    window.cervusNavChrome.restoreAfterDrawer?.();
  }

  function releaseScrollLockOnly() {
    if (!isMobileCartViewport() || document.body.dataset.cartScrollLocked !== 'true') return;

    const savedY = readLockedScrollY();
    window.cervusHeaderScroll?.syncScrollY?.(savedY);
    releaseMobileCartScrollLock(savedY);
    cartScrollLockY = savedY;
  }

  function closeCartDrawerForSwitch() {
    cancelCartCloseAnimation();

    if (cartBusy) {
      setCartBusy(false);
    }

    drawerOpen = false;
    drawer.setAttribute('aria-hidden', 'true');

    openButtons.forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
    });

    drawer.classList.add('is-closing');
    drawer.classList.remove('is-open');

    window.setTimeout(() => {
      drawer.classList.remove('is-closing');
      isClosingCart = false;
      cartCloseInProgress = false;
    }, 220);

    return Promise.resolve();
  }

  function closeCartDrawer(options = {}) {
    if (!drawerOpen && !drawer?.classList.contains('is-open')) return Promise.resolve();

    if (options.switchContext && isMobileCartViewport()) {
      return closeCartDrawerForSwitch();
    }

    if (options.immediate) {
      cancelCartCloseAnimation();
      if (cartBusy) setCartBusy(false);
      drawerOpen = false;
      drawer.classList.remove('is-open', 'is-closing');
      drawer.setAttribute('aria-hidden', 'true');
      openButtons.forEach((button) => {
        button.setAttribute('aria-expanded', 'false');
      });
      if (isMobileCartViewport()) {
        isClosingCart = true;
        cartCloseInProgress = true;
      }
      finishCartDrawerClose();
      return Promise.resolve();
    }

    setDrawerOpen(false);
    return Promise.resolve();
  }

  function setDrawerOpen(isOpen, options = {}) {
    const switchContext = options.switchContext === true;
    const managedOpen = options.managedOpen === true;
    if (!drawer) return Promise.resolve();

    if (!isOpen) {
      if (cartCloseInProgress) return Promise.resolve();
      if (!drawerOpen && !drawer.classList.contains('is-open')) return Promise.resolve();

      if (switchContext && isMobileCartViewport()) {
        return closeCartDrawerForSwitch();
      }

      if (cartBusy) {
        setCartBusy(false);
      }

      drawerOpen = false;
      drawer.setAttribute('aria-hidden', 'true');

      openButtons.forEach((button) => {
        button.setAttribute('aria-expanded', 'false');
      });

      if (isMobileCartViewport()) {
        isClosingCart = true;
        cartCloseInProgress = true;
        drawer.classList.remove('is-open', 'is-closing');
        finishCartDrawerClose();
        return Promise.resolve();
      }

      drawer.classList.remove('is-open');

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        finishCartDrawerClose();
        return Promise.resolve();
      }

      scheduleCartDrawerCloseFinish();
      return Promise.resolve();
    }

    cancelCartCloseAnimation();

    if (cartBusy) {
      setCartBusy(false);
    }

    if (drawerOpen || drawer.classList.contains('is-open')) {
      if (!switchContext || !drawer.classList.contains('is-closing')) {
        return Promise.resolve();
      }
    }

    if (switchContext) {
      isClosingCart = false;
      cartCloseInProgress = false;
    }

    drawerOpen = true;
    drawer.setAttribute('aria-hidden', 'false');

    openButtons.forEach((button) => {
      button.setAttribute('aria-expanded', 'true');
    });

    if (isMobileCartViewport()) {
      restoreMobileCartDrawerHitTargets();
      openMobileCartDrawerVisual();

      window.requestAnimationFrame(() => {
        portalCartDrawer();

        if (!document.body.dataset.cartScrollLocked) {
          lockCartScroll();
        }

        if (!switchContext && !managedOpen) {
          window.cervusBodyLock?.set?.('cart', true);
          syncCartNavChrome(true);
        }
      });
    } else {
      portalCartDrawer();
      lockCartScroll();
      window.cervusBodyLock?.set?.('cart', true);
      syncCartNavChrome(true);
      drawer.classList.add('is-open');
    }

    if (!switchContext) {
      window.cervusSearch?.close?.({ immediate: true });
      window.cervusNav?.close?.({ immediate: true });
      window.cervusNavDrawer?.close?.();
      window.cervusAccount?.close?.({ immediate: true });
    }

    return Promise.resolve();
  }

  async function openCartDrawerFromAdd() {
    if (isOpeningCart || isClosingCart || cartCloseInProgress) return;
    if (drawerOpen || drawer?.classList.contains('is-open')) return;

    isOpeningCart = true;

    try {
      await refreshCart();

      if (!drawerOpen && !drawer?.classList.contains('is-open')) {
        setDrawerOpen(true);
      }
    } finally {
      isOpeningCart = false;
    }
  }

  function setCartBusy(isBusy) {
    if (!drawer) return;
    cartBusy = isBusy;
    drawer.classList.toggle('cart-drawer--busy', isBusy);

    drawer.querySelectorAll('[data-cart-qty-decrease], [data-cart-qty-increase], [data-cart-remove]').forEach((control) => {
      control.disabled = isBusy;
    });

    const checkoutBtn = drawer.querySelector('[data-cart-cta]');
    if (checkoutBtn) checkoutBtn.disabled = isBusy;
  }

  function setCartDrawerView(cart) {
    const isEmpty = !cart || cart.item_count === 0;

    if (emptyWrap) {
      if (isEmpty) {
        emptyWrap.removeAttribute('hidden');
      } else {
        emptyWrap.setAttribute('hidden', '');
      }
    }

    if (footer) {
      footer.removeAttribute('hidden');
    }

    if (subtotalRow) {
      if (isEmpty) {
        subtotalRow.setAttribute('hidden', '');
      } else {
        subtotalRow.removeAttribute('hidden');
      }
    }

    if (cartCta) {
      cartCta.type = isEmpty ? 'button' : 'submit';
      cartCta.name = isEmpty ? '' : 'checkout';
      cartCta.dataset.cartCtaMode = isEmpty ? 'explore' : 'checkout';
      cartCta.textContent = isEmpty
        ? drawer.dataset.labelExplore || 'Seguir explorando'
        : drawer.dataset.labelCheckout || 'Continuar';
    }

    const continueBtn = drawer.querySelector('[data-cart-continue]');
    if (continueBtn) {
      if (isEmpty) {
        continueBtn.setAttribute('hidden', '');
      } else {
        continueBtn.removeAttribute('hidden');
      }
    }
  }

  async function continueShoppingFromEmptyCart() {
    const exploreUrl = drawer?.dataset.continueShoppingUrl || '/collections/all';

    if (isMobileCartViewport() && window.cervusMobileDrawerSwitch) {
      await window.cervusMobileDrawerSwitch.close('cart');
    } else {
      await setDrawerOpen(false);
    }

    window.location.assign(exploreUrl);
  }

  cartCta?.addEventListener('click', (event) => {
    if (cartCta.dataset.cartCtaMode === 'explore') {
      event.preventDefault();
      void continueShoppingFromEmptyCart();
    }
  });

  async function changeLineQuantity(lineKey, quantity) {
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: Math.max(0, quantity) }),
    });
    return response.ok;
  }

  async function applyQuantityChange(lineKey, quantity) {
    if (cartBusy) return;

    const isDrawerContext = !document.body.classList.contains('template-cart');
    if (isDrawerContext) setCartBusy(true);

    try {
      const ok = await changeLineQuantity(lineKey, quantity);
      if (!ok) return;

      if (document.body.classList.contains('template-cart')) {
        window.location.reload();
        return;
      }

      await refreshCart();
    } finally {
      if (isDrawerContext) setCartBusy(false);
    }
  }

  function bindDrawerQuantityControls(root) {
    root.querySelectorAll('[data-cart-qty-decrease]').forEach((button) => {
      button.addEventListener('click', () => {
        const lineKey = button.dataset.cartLine;
        const current = Number(button.dataset.cartQty) || 0;
        applyQuantityChange(lineKey, current - 1);
      });
    });

    root.querySelectorAll('[data-cart-qty-increase]').forEach((button) => {
      button.addEventListener('click', () => {
        const lineKey = button.dataset.cartLine;
        const current = Number(button.dataset.cartQty) || 0;
        applyQuantityChange(lineKey, current + 1);
      });
    });
  }

  function getCartFingerprint(cart) {
    if (!cart) return 'empty';

    const itemsSignature = cart.items
      .map((item) => `${item.key}:${item.quantity}:${item.final_line_price}`)
      .join('|');

    return `${cart.item_count};${cart.total_price};${itemsSignature}`;
  }

  function renderCart(cart) {
    if (!drawer || !itemsRoot) return;

    const fingerprint = getCartFingerprint(cart);
    const drawerIsOpen = drawerOpen || drawer?.classList.contains('is-open') === true;

    if (fingerprint === lastCartFingerprint) {
      return;
    }

    lastCartFingerprint = fingerprint;

    countEls.forEach((el) => {
      if (cart.item_count > 0) {
        el.hidden = false;
        el.textContent = String(cart.item_count);
      } else {
        el.hidden = true;
        el.textContent = '';
      }
    });

    setCartDrawerView(cart);

    if (cart.item_count === 0) {
      itemsRoot.innerHTML = '';
      return;
    }

    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

    itemsRoot.innerHTML = cart.items
      .map((item) => {
        const variantLabel = getCartVariantLabel(item.variant_title);
        const imageUrl = cartImageUrl(item.image);
        const imageMarkup = imageUrl
          ? `<img src="${escapeHtml(imageUrl)}" alt="" width="120" height="120" loading="lazy" decoding="async">`
          : '';

        return `
          <li class="cart-drawer__item">
            <a class="cart-drawer__item-image" href="${escapeHtml(item.url)}" tabindex="-1" aria-hidden="true">
              ${imageMarkup}
            </a>
            <div class="cart-drawer__item-body">
              <a class="cart-drawer__item-title type-ui" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
              ${variantLabel}
              <p class="cart-drawer__item-price type-ui">${formatMoney(item.final_line_price)}</p>
              <div class="cart-drawer__qty" data-cart-qty>
                <button
                  type="button"
                  class="cart-drawer__qty-btn type-ui"
                  data-cart-qty-decrease
                  data-cart-line="${escapeHtml(item.key)}"
                  data-cart-qty="${item.quantity}"
                  aria-label="${escapeHtml(labelDecrease)}"
                >
                  <span aria-hidden="true">−</span>
                </button>
                <span class="cart-drawer__qty-value type-ui" aria-label="${escapeHtml(labelQuantity)}">${item.quantity}</span>
                <button
                  type="button"
                  class="cart-drawer__qty-btn type-ui"
                  data-cart-qty-increase
                  data-cart-line="${escapeHtml(item.key)}"
                  data-cart-qty="${item.quantity}"
                  aria-label="${escapeHtml(labelIncrease)}"
                >
                  <span aria-hidden="true">+</span>
                </button>
              </div>
              <button
                type="button"
                class="cart-drawer__item-remove type-ui"
                data-cart-remove="${escapeHtml(item.key)}"
              >
                ${escapeHtml(labelRemove)}
              </button>
            </div>
          </li>`;
      })
      .join('');

    bindDrawerQuantityControls(itemsRoot);

    itemsRoot.querySelectorAll('[data-cart-remove]').forEach((button) => {
      button.addEventListener('click', () => applyQuantityChange(button.dataset.cartRemove, 0));
    });

    if (isMobileCartViewport() && (drawerIsOpen || isOpeningCart)) {
      itemsRoot.dataset.cartItemsStable = 'true';
    } else {
      delete itemsRoot.dataset.cartItemsStable;
    }
  }

  async function refreshCart() {
    if (cartFetchPromise) return cartFetchPromise;

    cartFetchPromise = (async () => {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return null;
      const cart = await response.json();
      renderCart(cart);
      return cart;
    })();

    try {
      return await cartFetchPromise;
    } finally {
      cartFetchPromise = null;
    }
  }

  async function refreshCartWithBusy() {
    if (cartBusy) return;
    setCartBusy(true);
    try {
      await refreshCart();
    } finally {
      setCartBusy(false);
    }
  }

  openButtons.forEach((button) => {
    const isMobileHeaderCart = Boolean(button.closest('.editorial-nav__mobile-slot--cart'));

    button.addEventListener('click', async (event) => {
      if (isMobileHeaderCart && window.cervusMobileDrawerSwitch) {
        event.preventDefault();
        if (window.cervusMobileDrawerSwitch.isSwitching?.()) return;
        await window.cervusMobileDrawerSwitch.toggle('cart');
        return;
      }

      if (isMobileHeaderCart) {
        if (drawerOpen || drawer.classList.contains('is-open')) {
          setDrawerOpen(false);
          return;
        }

        if (isOpeningCart || isClosingCart || cartCloseInProgress) return;

        isOpeningCart = true;

        try {
          await refreshCart();
          await setDrawerOpen(true);
        } finally {
          isOpeningCart = false;
        }

        return;
      }

      await refreshCartWithBusy();
      setDrawerOpen(true);
    });
  });

  closeTargets.forEach((target) => {
    target.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (isMobileCartViewport() && window.cervusMobileDrawerSwitch) {
        window.cervusMobileDrawerSwitch.close('cart');
        return;
      }
      setDrawerOpen(false);
    });
  });

  drawer?.querySelector('.cart-drawer__panel')?.addEventListener('click', (event) => {
    event.stopPropagation();
  });

  window.addEventListener('pagehide', () => {
    if (!drawerOpen && !drawer?.classList.contains('is-open')) return;
    drawerOpen = false;
    drawer?.classList.remove('is-open');
    finishCartDrawerClose();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !drawerOpen || isSearchOverlayOpen()) return;
    event.preventDefault();
    if (isMobileCartViewport() && window.cervusMobileDrawerSwitch) {
      window.cervusMobileDrawerSwitch.close('cart');
      return;
    }
    setDrawerOpen(false);
  });

  document.addEventListener('cart:refresh', () => {
    if (isOpeningCart || cartFetchPromise) return;

    if (drawerOpen) {
      refreshCartWithBusy();
      return;
    }
    refreshCart();
  });

  window.cervusCart = {
    close: (options) => closeCartDrawer(options),
    isOpen: () => drawerOpen || drawer?.classList.contains('is-open') === true,
    isClosing: () => isClosingCart,
    isOpening: () => isOpeningCart,
    releaseScrollLock: releaseScrollLockOnly,
  };

  window.cervusMobileDrawerSwitch?.register?.('cart', {
    isOpen: () => drawerOpen || drawer?.classList.contains('is-open') === true,
    open: async (options) => {
      if (isOpeningCart) return;

      const switchContext = options?.switchContext === true;
      if (!switchContext && (isClosingCart || cartCloseInProgress)) return;

      isOpeningCart = true;

      try {
        await refreshCart();
        await setDrawerOpen(true, {
          switchContext,
          managedOpen: true,
        });
      } finally {
        isOpeningCart = false;
      }
    },
    close: async (options) => {
      await closeCartDrawer(options);
    },
  });

  portalCartDrawer();

  const cartPageForm = document.querySelector('[data-cart-page-form]');
  if (cartPageForm) {
    cartPageForm.querySelectorAll('[data-cart-page-qty]').forEach((group) => {
      const input = group.querySelector('[data-cart-page-qty-input]');
      const lineKey = input?.dataset.lineKey;
      if (!input || !lineKey) return;

      group.querySelector('[data-cart-page-qty-decrease]')?.addEventListener('click', () => {
        const current = Number(input.value) || 0;
        applyQuantityChange(lineKey, current - 1);
      });

      group.querySelector('[data-cart-page-qty-increase]')?.addEventListener('click', () => {
        const current = Number(input.value) || 0;
        applyQuantityChange(lineKey, current + 1);
      });
    });
  }

  async function submitBoticaForm(form) {
    const submitBtn = form.querySelector('[data-product-submit]');
    if (submitBtn) submitBtn.disabled = true;

    const formData = new FormData(form);
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: { Accept: 'application/json' },
    });

    if (response.ok) {
      announce('Added to selection');
      await openCartDrawerFromAdd();
    } else if (submitBtn) {
      submitBtn.disabled = false;
    }

    if (submitBtn && response.ok) {
      const variantInput = form.querySelector('[data-product-variant-id]');
      if (variantInput && submitBtn.dataset.labelAdd) {
        submitBtn.disabled = false;
        submitBtn.textContent = submitBtn.dataset.labelAdd;
      }
    }
  }

  document.querySelectorAll('[data-botica-product-form]').forEach((form) => {
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      await submitBoticaForm(form);
    });
  });

  /* —— PDP —— */
  const productForm = document.querySelector('[data-product-form]');
  if (productForm) {
    const variantsJson = productForm.querySelector('[data-product-variants]');
    const variants = variantsJson ? JSON.parse(variantsJson.textContent) : [];
    const variantSelect = productForm.querySelector('[data-product-variant-select]');
    const priceEl = document.querySelector('[data-product-price]');
    const submitBtn = productForm.querySelector('[data-product-submit]');
    const quantityInput = productForm.querySelector('[data-product-quantity]');
    const heroImageRoot = document.querySelector('[data-product-hero-image]');
    const galleryTriggers = document.querySelectorAll('[data-product-gallery-trigger]');
    const labelAdd = submitBtn?.dataset.labelAdd || 'Add';
    const labelUnavailable = submitBtn?.dataset.labelUnavailable || 'Unavailable';

    function syncVariant(variantId) {
      const variant = variants.find((entry) => String(entry.id) === String(variantId));
      if (!variant) return;

      if (priceEl) priceEl.textContent = formatMoney(variant.price);
      if (submitBtn) {
        submitBtn.disabled = !variant.available;
        submitBtn.textContent = variant.available ? labelAdd : labelUnavailable;
      }
    }

    if (variantSelect) {
      variantSelect.addEventListener('change', () => syncVariant(variantSelect.value));
    }

    if (quantityInput) {
      quantityInput.addEventListener('change', () => {
        const min = Number(quantityInput.min) || 1;
        const value = Number(quantityInput.value);
        if (!Number.isFinite(value) || value < min) {
          quantityInput.value = String(min);
        }
      });
    }

    galleryTriggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const heroImg = heroImageRoot?.querySelector('.editorial-image__frame');
        const src = trigger.dataset.imageSrc;
        const srcset = trigger.dataset.imageSrcset;
        if (!heroImg || !src) return;

        heroImg.classList.remove('is-visible');
        heroImg.src = src;
        if (srcset) heroImg.srcset = srcset;
        heroImg.loading = 'eager';

        if (heroImg.complete && heroImg.naturalWidth > 0) {
          heroImg.classList.add('is-visible');
        } else {
          heroImg.addEventListener(
            'load',
            () => {
              heroImg.classList.add('is-visible');
            },
            { once: true }
          );
        }

        galleryTriggers.forEach((btn) => btn.classList.remove('is-active'));
        trigger.classList.add('is-active');
      });
    });

    productForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      await submitBoticaForm(productForm);

      if (submitBtn) {
        const variantId = new FormData(productForm).get('id');
        syncVariant(variantId);
      }
    });
  }
})();
